/**
 * The single switch for dashboard performance tracing — in every environment,
 * production included. Turning it on makes the boot/prefetch instrumentation
 * verbose in the console and uploads the finished trace to Sentry (see
 * perfTrace.ts). It never changes what the app does, only what it reports.
 *
 * Enable with any ONE of:
 *   - open any page with `?ahoy_perf=1` (persists; `?ahoy_perf=0` clears it)
 *   - `localStorage.setItem('ahoy:perf', '1')` then reload
 *   - `window.__ahoy.perf.enable()` in DevTools, then reload
 *
 * The older per-module keys `boot-debug` (bootMetrics) and `debug_prefetch`
 * (prefetchDebug) are honoured as aliases so existing instructions keep
 * working — there is one flag, not three.
 *
 * The value is read once and cached: it is consulted on hot paths (every
 * prefetch debug line, every boot mark) and must cost nothing when off.
 */

export const PERF_FLAG_STORAGE_KEY = 'ahoy:perf';
export const PERF_FLAG_QUERY_PARAM = 'ahoy_perf';
const PERF_UPLOAD_SESSION_KEY = 'ahoy:perf:uploaded';

const LEGACY_STORAGE_KEYS = ['boot-debug', 'debug_prefetch'] as const;

let cachedEnabled: boolean | null = null;

function readFromStorage(): boolean {
  try {
    if (localStorage.getItem(PERF_FLAG_STORAGE_KEY) === '1') {
      return true;
    }
    return LEGACY_STORAGE_KEYS.some(key => localStorage.getItem(key) === '1');
  } catch {
    // localStorage unavailable (privacy mode, SSR) — tracing stays off.
    return false;
  }
}

/** Whether perf tracing is on for this page load. Cached after first read. */
export function isPerfTraceEnabled(): boolean {
  if (cachedEnabled === null) {
    cachedEnabled = readFromStorage();
  }
  return cachedEnabled;
}

/** Persist the flag on. Takes effect for verbose logging immediately; the
 *  boot trace itself needs a reload to capture the boot sequence. */
export function enablePerfTrace(): void {
  try {
    localStorage.setItem(PERF_FLAG_STORAGE_KEY, '1');
  } catch {
    // Storage unavailable — in-memory flag still flips for this page.
  }
  cachedEnabled = true;
}

/** Clear the flag and every legacy alias. */
export function disablePerfTrace(): void {
  try {
    localStorage.removeItem(PERF_FLAG_STORAGE_KEY);
    for (const key of LEGACY_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing persisted to clear.
  }
  cachedEnabled = false;
}

/**
 * Apply `?ahoy_perf=1` / `?ahoy_perf=0` from the URL to storage. Call once,
 * as early as possible in the page (prefetch.ts runs before main.tsx), so the
 * very first mark of the boot sequence already sees the flag. Returns the
 * resulting state.
 */
export function applyPerfQueryParam(
  search: string = window.location.search
): boolean {
  try {
    const value = new URLSearchParams(search).get(PERF_FLAG_QUERY_PARAM);
    if (value === '1') {
      enablePerfTrace();
    } else if (value === '0') {
      disablePerfTrace();
    }
  } catch {
    // Malformed search string — leave the stored flag alone.
  }
  cachedEnabled = null;
  return isPerfTraceEnabled();
}

/**
 * Whether this tab session has already uploaded a Sentry perf trace.
 * Throttles repeat uploads for long-lived persisted-flag sessions to once per tab.
 */
export function isPerfUploadThrottled(): boolean {
  try {
    return sessionStorage.getItem(PERF_UPLOAD_SESSION_KEY) === '1';
  } catch {
    // sessionStorage unavailable — treat as not throttled.
    return false;
  }
}

/** Record that this tab session has uploaded a trace (throttles subsequent loads). */
export function markPerfUploadDone(): void {
  try {
    sessionStorage.setItem(PERF_UPLOAD_SESSION_KEY, '1');
  } catch {
    // sessionStorage unavailable — throttle won't persist for this tab.
  }
}

/** Test-only — drop the cached value so the next read hits storage again. */
export function resetPerfFlagForTest(): void {
  cachedEnabled = null;
  try {
    sessionStorage.removeItem(PERF_UPLOAD_SESSION_KEY);
  } catch {
    // storage unavailable
  }
}
